import { useCallback, useRef } from 'react';
import axios from 'axios';
import * as Sentry from '@sentry/browser';

import { useAppDispatch } from 'redux/store';
import { attachVideoTurn, VideoTurnRole } from 'redux/actions/ai-role-play';
import { wrapThunkAction } from 'redux/utils';
import { config } from '@config/config.json';

const S3_URL = `${config.s3.protocol}://s3.amazonaws.com/${config.s3.bucket}/`;

const MAX_UPLOAD_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 2000;

export type VideoTurnUpload = {
  blob: Blob;
  durationSeconds: number;
  turnId: string;
  // Whose side of the conversation this segment shows: the learner's camera or
  // the AI avatar's video.
  role: VideoTurnRole;
  rolePlayId: string;
  submissionId: number;
  catalogId: string;
};

type UseVideoTurnUploaderReturn = {
  // Queues one turn segment's webm for background upload (presign → S3 POST →
  // attach). Items are processed sequentially and retried with backoff; an item
  // that exhausts its retries is dropped with a warning so the conversation and
  // the remaining queue keep going.
  enqueueTurnUpload: (upload: VideoTurnUpload) => void;
};

const delay = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

/**
 * Background upload queue for video turn segments (learner camera and AI
 * avatar alike). Each segment's webm goes to S3 via the presigned-POST flow
 * (mirroring shared/hooks/use-upload-file), then is attached to the submission
 * in Rails with its role so the server can merge the two-sided conversation
 * video once it completes.
 */
const useVideoTurnUploader = (): UseVideoTurnUploaderReturn => {
  const dispatch = useAppDispatch();

  const queueRef = useRef<VideoTurnUpload[]>([]);
  const isProcessingRef = useRef(false);

  const uploadTurn = useCallback(async (upload: VideoTurnUpload) => {
    const { blob, durationSeconds, turnId, role, rolePlayId, submissionId, catalogId } = upload;
    // The role keeps the two segments of one turn from colliding on S3.
    const file = new File([blob], `${turnId}-${role}.webm`, { type: blob.type || 'video/webm' });

    // 1. Presigned POST fields from Rails
    const uploadFields = await axios
      .put('/s3_direct_upload/upload_fields.json', {
        fileName: file.name,
        s3Namespace: `ai_role_play_submissions/${rolePlayId}/video-turns`,
      })
      .then((response) => response.data);

    // 2. Direct S3 POST — fetch keeps the request free of axios interceptor headers
    const formData = new FormData();
    const fields = {
      key: uploadFields.key,
      AWSAccessKeyId: uploadFields.aWSAccessKeyId,
      acl: uploadFields.acl,
      policy: uploadFields.policy,
      signature: uploadFields.signature,
      'Content-Type': file.type,
      'X-Requested-With': uploadFields.xRequestedWith,
      utf8: '✓',
      success_action_status: uploadFields.successActionStatus,
      file,
    };
    Object.keys(fields).forEach((key) => formData.append(key, fields[key]));

    const s3Response = await fetch(S3_URL, { method: 'POST', body: formData });
    if (!s3Response.ok) {
      throw new Error(`S3 upload failed with status ${s3Response.status}`);
    }

    // 3. Attach the uploaded turn segment to the submission in Rails
    await wrapThunkAction(dispatch(attachVideoTurn({
      catalogId,
      submissionId,
      turnId,
      role,
      s3Key: uploadFields.key,
      duration: durationSeconds,
      contentType: file.type,
      fileSize: file.size,
    })));
  }, [dispatch]);

  const processQueue = useCallback(async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      while (queueRef.current.length > 0) {
        const upload = queueRef.current[0];

        let lastError: unknown = null;
        for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt += 1) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await uploadTurn(upload);
            lastError = null;
            break;
          } catch (error) {
            lastError = error;
            if (attempt < MAX_UPLOAD_ATTEMPTS) {
              // eslint-disable-next-line no-await-in-loop
              await delay(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
            }
          }
        }

        if (lastError) {
          // Dropped — the segment's video is lost but the conversation (and the
          // rest of the queue) must keep going.
          // eslint-disable-next-line no-console
          console.warn(`Video turn upload failed after ${MAX_UPLOAD_ATTEMPTS} attempts (turn ${upload.turnId}, role ${upload.role})`, lastError);
          Sentry.captureException(lastError);
        }

        queueRef.current.shift();
      }
    } finally {
      isProcessingRef.current = false;
    }
  }, [uploadTurn]);

  const enqueueTurnUpload = useCallback((upload: VideoTurnUpload) => {
    queueRef.current.push(upload);
    processQueue();
  }, [processQueue]);

  return { enqueueTurnUpload };
};

export default useVideoTurnUploader;
